import React, { useCallback, useEffect, useState } from 'react'
import { useMemo } from 'react'
import { ApolloQueryResult } from '@apollo/client'
import { Maybe } from '@graphql-tools/utils'
import { slugs } from '@/constants'
import { usePlayerMoments } from '@/organisms/CollectiblesPlayer'
import { useEngagementMoment } from '@/organisms/CollectiblesPlayer/CollectiblesPlayerEngagement/CollectiblesPlayerEngagementContext/useEngagementMoment'
import { GetMomentOwnersQuery, GetMomentOwnersQueryVariables, Moment } from '@/types/codegen-federation'
import { ReactFCC } from '@/types/react'
import { useSafeTrack } from '@/utils/analytics'

interface CollectiblesPlayerEngagementPanelProps {
  defaultEngagementDetail: Maybe<EngagementDetail>
  defaultMoment: Maybe<Moment>
  projectSlug: string
  isPanelOpen?: boolean
  userStoryId?: string
}

const noop = () => undefined

interface CollectiblesPlayerEngagementValues extends CollectiblesPlayerEngagementPanelProps {
  closeEngagementPanel: () => void
  engagementMoment: Maybe<Moment>
  engagementMomentDetails: Maybe<GetMomentOwnersQuery>
  engagementPanelState: CollectiblesPlayerEngagementPanelState
  isEngagementPanelOpen: boolean
  isLoadingEngagementMomentDetails: boolean
  openEngagementPanel: (moment: Moment) => void
  refetchEngagementMomentDetails: (
    variables: Pick<GetMomentOwnersQueryVariables, 'momentId' | 'includeMyStory'>,
  ) => Promise<ApolloQueryResult<GetMomentOwnersQuery>> | Promise<void> | undefined
  showUserStoriesList: () => void
  closeUserStoriesList: () => void
  updateEngagementMoment: (moment: Moment) => void
  getMoreStories: (options: {
    take: number
    skip: number
  }) => Promise<ApolloQueryResult<GetMomentOwnersQuery>> | Promise<void> | undefined
}

export const CollectiblesPlayerEngagementPanelContext = React.createContext<CollectiblesPlayerEngagementValues>({
  closeEngagementPanel: noop,
  closeUserStoriesList: noop,
  defaultEngagementDetail: null,
  defaultMoment: null,
  engagementMoment: null,
  engagementMomentDetails: null,
  engagementPanelState: 'collectible-scene',
  isEngagementPanelOpen: false,
  isLoadingEngagementMomentDetails: false,
  openEngagementPanel: noop,
  updateEngagementMoment: noop,
  projectSlug: slugs.forYourConsideration,
  refetchEngagementMomentDetails: () => Promise.resolve(),
  showUserStoriesList: noop,
  getMoreStories: () => Promise.resolve(),
  isPanelOpen: true,
})

export type CollectiblesPlayerEngagementPanelState = 'collectible-scene' | 'stories-list' | 'off'

export type EngagementDetailUser = {
  id: string
  isOwner: Maybe<boolean>
  firstName: Maybe<string>
  lastName: Maybe<string>
  image: Maybe<string>
}

export type EngagementDetailStory = {
  videoUri: Maybe<string>
  text: Maybe<string>
}

export type EngagementDetail = {
  startTime: number
  user: Maybe<EngagementDetailUser>
  story: Maybe<EngagementDetailStory>
}

export const CollectiblesPlayerEngagementProvider: ReactFCC<CollectiblesPlayerEngagementPanelProps> = ({
  children,
  defaultEngagementDetail,
  defaultMoment,
  projectSlug,
  isPanelOpen = true,
  userStoryId,
}) => {
  const track = useSafeTrack()
  const defaultPanelState = 'collectible-scene'
  const [canOpenEngagementPanel, setCanOpenEngagementPanel] = useState<boolean>(isPanelOpen)
  const [engagementPanelState, setEngagementPanelState] =
    useState<CollectiblesPlayerEngagementPanelState>(defaultPanelState)
  const [engagementMoment, setEngagementMoment] = useState<Maybe<Moment>>(defaultMoment)
  const { data, loading, refetch, fetchMore } = useEngagementMoment({ momentId: engagementMoment?.id })
  const { canDisplayScenes } = usePlayerMoments()

  useEffect(() => {
    if (isPanelOpen) {
      setCanOpenEngagementPanel(true)
    }
  }, [isPanelOpen])

  const openEngagementPanel = useCallback(
    (moment: Moment) => {
      track('Engagement Panel Opened', {
        momentId: moment?.id,
        episodeGuid: moment?.episode?.guid,
      })

      setEngagementMoment(moment)
      setCanOpenEngagementPanel(true)
      setEngagementPanelState('collectible-scene')
    },
    [track],
  )

  const updateEngagementMoment = useCallback((moment: Moment) => {
    setEngagementMoment(moment)
  }, [])

  const showUserStoriesList = useCallback(() => {
    setEngagementPanelState('stories-list')
  }, [])

  const closeUserStoriesList = useCallback(async () => {
    await fetchMore({ take: 30, skip: 0 })
    setEngagementPanelState('collectible-scene')
  }, [fetchMore])

  const closeEngagementPanel = useCallback(() => {
    setCanOpenEngagementPanel(isPanelOpen)
  }, [isPanelOpen])

  const getMoreStories = useCallback(
    ({ take, skip }: { take: number; skip: number }) => {
      return fetchMore({ take, skip })
    },
    [fetchMore],
  )

  const value = useMemo(() => {
    const isEngagementPanelOpen = canDisplayScenes && canOpenEngagementPanel
    return {
      closeEngagementPanel,
      closeUserStoriesList,
      defaultEngagementDetail,
      defaultMoment,
      engagementMoment,
      engagementMomentDetails: data,
      engagementPanelState,
      isEngagementPanelOpen,
      isLoadingEngagementMomentDetails: loading,
      openEngagementPanel,
      updateEngagementMoment,
      projectSlug,
      refetchEngagementMomentDetails: refetch,
      showUserStoriesList,
      getMoreStories,
      userStoryId,
    }
  }, [
    canDisplayScenes,
    canOpenEngagementPanel,
    closeEngagementPanel,
    closeUserStoriesList,
    defaultEngagementDetail,
    defaultMoment,
    engagementMoment,
    data,
    engagementPanelState,
    loading,
    openEngagementPanel,
    updateEngagementMoment,
    projectSlug,
    refetch,
    showUserStoriesList,
    getMoreStories,
    userStoryId,
  ])

  return (
    <CollectiblesPlayerEngagementPanelContext.Provider value={value}>
      {children}
    </CollectiblesPlayerEngagementPanelContext.Provider>
  )
}

export const useCollectiblesPlayerEngagement = () => {
  return React.useContext(CollectiblesPlayerEngagementPanelContext)
}
